import React, { useEffect, useRef, useState } from 'react';
import rrwebPlayer from 'rrweb-player';
import 'rrweb-player/dist/style.css';
import './simple-responsive-player.css';
import { loadSessionEvents, EventWithTime } from '@/lib/rrwebUtils';
import { Loader2, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SimpleResponsiveRrwebPlayerProps {
  sessionEventsStoragePath: string;
  autoPlay?: boolean;
  className?: string;
  onPlayerReady?: (player: any) => void;
  onError?: (error: string) => void;
}

export function SimpleResponsiveRrwebPlayer({
  sessionEventsStoragePath,
  autoPlay = false,
  className,
  onPlayerReady,
  onError
}: SimpleResponsiveRrwebPlayerProps) {
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const playerInstanceRef = useRef<any>(null);
  const [events, setEvents] = useState<EventWithTime[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  // Load events - only depends on path
  useEffect(() => {
    let isMounted = true;
    
    const loadEvents = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        console.log('Loading events from:', sessionEventsStoragePath);
        const loadedEvents = await loadSessionEvents(sessionEventsStoragePath);
        
        if (!isMounted) return; // Component unmounted during load
        
        if (loadedEvents && loadedEvents.length > 0) {
          console.log('Loaded', loadedEvents.length, 'events');
          setEvents(loadedEvents);
        } else {
          throw new Error('No valid events found in session file');
        }
      } catch (err) {
        if (!isMounted) return;
        console.error('Error loading events:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to load session events';
        setError(errorMessage);
        onError?.(errorMessage);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    if (sessionEventsStoragePath) {
      loadEvents();
    }

    return () => {
      isMounted = false;
    };
  }, [sessionEventsStoragePath, onError]);

  // Measure container dimensions
  useEffect(() => {
    if (!playerContainerRef.current) return;

    const measureContainer = () => {
      if (playerContainerRef.current) {
        const width = playerContainerRef.current.clientWidth;
        const height = playerContainerRef.current.clientHeight;
        
        console.log('Measured container dimensions:', { width, height });
        
        if (width > 0 && height > 0) {
          setContainerSize({ width, height });
        }
      }
    };

    // Use setTimeout to ensure the container is rendered
    setTimeout(measureContainer, 100);
    
    const resizeObserver = new ResizeObserver(measureContainer);
    resizeObserver.observe(playerContainerRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  // Initialize player - depends on events and container size
  useEffect(() => {
    if (!events || !playerContainerRef.current || playerInstanceRef.current) {
      console.log('Player initialization skipped:', {
        hasEvents: !!events,
        hasContainer: !!playerContainerRef.current,
        hasInstance: !!playerInstanceRef.current,
        containerSize
      });
      return;
    }

    console.log('Initializing rrweb-player with', events.length, 'events');
    console.log('Container size:', containerSize);

    try {
      // Clear container
      playerContainerRef.current.innerHTML = '';

      // Get current container dimensions
      const containerElement = playerContainerRef.current;
      const computedStyle = window.getComputedStyle(containerElement);
      const containerRect = containerElement.getBoundingClientRect();
      
      // Use getBoundingClientRect for accurate dimensions
      const currentWidth = containerRect.width;
      const currentHeight = containerRect.height;
      
      console.log('Using dimensions for player:', { 
        width: currentWidth, 
        height: currentHeight,
        clientWidth: containerElement.clientWidth,
        clientHeight: containerElement.clientHeight,
        boundingRect: containerRect
      });

      // Initialize rrweb-player with explicit dimensions
      const player = new rrwebPlayer({
        target: playerContainerRef.current,
        props: {
          events,
          UNSAFE_replayCanvas: true,
          autoPlay: false, // Always start paused to avoid media conflicts
          showController: true,
          speedOption: [0.5, 1, 1.5, 2, 4],
          width: Math.max(currentWidth, 800), // Ensure minimum width
          height: Math.max(currentHeight, 450), // Ensure minimum height
          // Mouse trail configuration
          mouseTail: {
            duration: 500,
            lineCap: 'round',
            lineWidth: 3,
            strokeStyle: 'red'
          }
        }
      });

      playerInstanceRef.current = player;

      // Debug: Check if mouseTail config is working
      setTimeout(() => {
        try {
          const replayer = player.getReplayer();
          console.log('Underlying replayer config:', {
            mouseTail: replayer?.config?.mouseTail,
            hasMouseTail: !!replayer?.mouseTail
          });
          
          // Check if mouse tail canvas element exists
          const mouseCanvas = playerContainerRef.current?.querySelector('canvas.replayer-mouse-tail');
          console.log('Mouse trail canvas found:', !!mouseCanvas);
          
          // Also check for all canvases
          const allCanvases = playerContainerRef.current?.querySelectorAll('canvas');
          console.log('All canvases in container:', allCanvases.length);
          Array.from(allCanvases).forEach((canvas, i) => {
            console.log(`Canvas ${i}:`, {
              className: canvas.className,
              width: canvas.width,
              height: canvas.height,
              style: canvas.getAttribute('style')
            });
          });
        } catch (e) {
          console.warn('Could not access replayer for debugging:', e);
        }
      }, 500);

      // Apply CSS fixes after player initialization (Sentry approach)
      setTimeout(() => {
        if (playerContainerRef.current) {
          console.log('Applying post-initialization fixes...');
          
          // Check what elements were created
          const allElements = playerContainerRef.current.querySelectorAll('*');
          console.log('Elements in container after player init:', Array.from(allElements).map(el => el.tagName.toLowerCase()));
          
          // Fix positioning issues with rrweb-player internal elements
          const replayerWrapper = playerContainerRef.current.querySelector('.replayer-wrapper');
          if (replayerWrapper) {
            console.log('Found replayer-wrapper, applying fixes');
            const wrapperElement = replayerWrapper as HTMLElement;
            wrapperElement.style.position = 'absolute';
            wrapperElement.style.width = '100%';
            wrapperElement.style.height = '100%';
            wrapperElement.style.left = '0';
            wrapperElement.style.top = '0';
            wrapperElement.style.transform = 'none';
            wrapperElement.style.inset = '0';
            console.log('Replayer wrapper dimensions after fix:', {
              width: wrapperElement.offsetWidth,
              height: wrapperElement.offsetHeight,
              rect: wrapperElement.getBoundingClientRect()
            });
          } else {
            console.log('No replayer-wrapper found');
          }

          const iframe = playerContainerRef.current.querySelector('iframe');
          if (iframe) {
            console.log('Found iframe, applying fixes');
            iframe.style.position = 'absolute';
            iframe.style.top = '0';
            iframe.style.left = '0';
            iframe.style.width = '100%';
            iframe.style.height = '100%';
            iframe.style.transform = 'none';
            iframe.style.border = 'none';
          } else {
            console.log('No iframe found - this might be the issue!');
          }

          const rrPlayer = playerContainerRef.current.querySelector('.rr-player');
          if (rrPlayer) {
            console.log('Found rr-player, applying fixes');
            const playerElement = rrPlayer as HTMLElement;
            playerElement.style.position = 'absolute';
            playerElement.style.width = '100%';
            playerElement.style.height = '100%';
            playerElement.style.inset = '0';
            console.log('RR Player dimensions after fix:', {
              width: playerElement.offsetWidth,
              height: playerElement.offsetHeight,
              rect: playerElement.getBoundingClientRect()
            });
          } else {
            console.log('No rr-player found');
          }
        }
      }, 100);

      console.log('Player initialized successfully');
      onPlayerReady?.(player);

    } catch (err) {
      console.error('Error initializing player:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to initialize player';
      setError(errorMessage);
      onError?.(errorMessage);
    }

    // Cleanup
    return () => {
      console.log('Cleaning up player');
      if (playerInstanceRef.current) {
        try {
          // rrweb-player cleanup
          playerInstanceRef.current.$destroy?.();
          playerInstanceRef.current = null;
        } catch (e) {
          console.warn('Error during player cleanup:', e);
        }
      }
    };
  }, [events, containerSize, autoPlay, onPlayerReady, onError]);

  if (isLoading) {
    return (
      <div className={cn(
        "flex items-center justify-center min-h-[400px] bg-black rounded-lg border border-gray-800",
        className
      )}>
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
          <p className="text-gray-400">Loading replay...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn(
        "flex items-center justify-center min-h-[400px] bg-black rounded-lg border border-gray-800",
        className
      )}>
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <div className="text-red-400 mb-2">Error loading replay</div>
          <p className="text-gray-400 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("w-full h-full", className)}>
      <div className="w-full h-full bg-black overflow-hidden">
        <div 
          ref={playerContainerRef}
          className="w-full h-full simple-responsive-player"
          style={{ 
            position: 'absolute',
            inset: '0',
            overflow: 'hidden'
          }}
        />
      </div>
    </div>
  );
}
